import {
  EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS,
  EXTRACTION_BEST_GUESS_INSTRUCTION,
  EXTRACTION_SINGLE_SHEET_CONFIDENCE_INSTRUCTIONS,
  EXTRACTION_STUDENT_NAME_INSTRUCTIONS,
} from './extractionSharedInstructions'

/**
 * System prompt sent with a single photographed handwritten sheet, routed to assessments of
 * type `Handwritten`, extracting the student name and transcribed answers. Interpolated with
 * `templateReplace` (`{{maxQuestion}}` / `{{questionTypes}}` / `{{openRangeInstruction}}` tokens)
 * before the vision call.
 *
 * Carries transcription instructions ONLY — no bubble/mark-detection
 * guidance, since a `Handwritten` assessment's sheets carry no OMR circles
 * to detect. Compare `BUBBLE_SHEET_PROMPT_DEFAULT`, which carries the
 * inverse: mark-detection instructions only.
 *
 * `data.confidence` is REQUIRED-AND-NULLABLE on `EXTRACTED_SHEET_SCHEMA` (`extractedSheetSchema.ts`),
 * the schema this route and the batch route share — omitting it from the documented payload here
 * fails the schema-constrained AI call with "No object generated: response did not match schema."
 *
 * `{{openRangeInstruction}}` sits flush against the prior sentence, with no
 * static line break of its own — `buildOpenRangeInstruction` supplies its
 * own leading blank line when non-empty, so a key with no Open range renders
 * a byte-identical prompt (`{{openRangeInstruction}}` resolves to `''`).
 */
export const HANDWRITTEN_SHEET_PROMPT_DEFAULT = `You are an answer sheet grading assistant. Extract the student name and all answers from this handwritten student answer sheet image - the student's answers are written by hand, not marked on a bubble form.

Return ONLY a raw JSON object with this exact shape (no markdown fences, no commentary):
{
  "data": {
    "studentName": <string or null>,
    "confidence": <confidence 0-1 for this sheet>,
    "answers": [
      [<questionNumber>, "<answer>", <confidence 0-1>]
    ]
  },
  "confidence": <overall confidence 0-1>
}

${EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS}

${EXTRACTION_SINGLE_SHEET_CONFIDENCE_INSTRUCTIONS}

Questions to extract: 1 to {{maxQuestion}}

Expected answer shape per question:
{{questionTypes}}

${EXTRACTION_STUDENT_NAME_INSTRUCTIONS}

HANDWRITTEN ANSWERS: read the letter, word, or list the student wrote. Be careful with handwriting.

MULTIPLE CHOICE ANSWERS: students print their answer in uppercase. Distinguish B from D by bowl count: B has two bowls, D has one. A single-bowl glyph whose vertical stroke rises above the bowl is D, not B, even if it resembles a lowercase "b". Report the letter exactly as read - never convert its case.

NUMERIC ANSWERS: when an answer is a number, return the digit(s), never a look-alike letter - 0 (zero) not O, 1 (one) not l or I, 2 (two) not Z, 5 (five) not S, 6 (six) not G.

${EXTRACTION_BEST_GUESS_INSTRUCTION}{{openRangeInstruction}}`
